import { watch, type WatchStopHandle } from 'vue'
import { isElectron } from '@/utils/env'
import { createRouter, createWebHashHistory, createWebHistory } from 'vue-router'
import { keyboardRoutes } from './keyboard'
import { mouseRoutes } from './mouse'
import { useAdminOnly } from '@/composables/useAdminOnly'
import { baseRoutes } from './base.ts'
import { deviceTypeGuard, adminOnlyGuard, keyboardFeatureGuard } from './guards'
import { getTestRoutes } from './test'

// 测试路由：生产构建（未设置 VITE_INCLUDE_TEST）时 getTestRoutes() 返回空数组
const { childRoutes: testChildRoutes, topRoutes: testTopRoutes } = getTestRoutes()

const router = createRouter({
  history: isElectron() ? createWebHashHistory(import.meta.env.BASE_URL) : createWebHistory('/'),
  routes: [
    {
      path: '/',
      component: () => import('@/views/index.vue'),
      children: [
        { path: '/:pathMatch(.*)*', name: '404', component: () => import('@/views/404/index.vue') },
        {
          path: '',
          alias: 'home',
          name: 'home',
          component: () => import('@/views/home/index.vue'),
          meta: { indexable: true },
        },
        {
          path: '/device',
          name: 'device',
          component: () => import('@/views/device/index.vue'),
          meta: { parentKey: 'device' },
          children: [...keyboardRoutes, ...mouseRoutes, ...baseRoutes],
        },
        {
          path: 'headerConsole',
          name: 'headerConsole',
          component: () => import('@/views/header/components/HeaderConsole.vue'),
        },
        {
          path: 'login',
          name: 'login',
          component: () => import('@/views/user/login/index.vue'),
        },
        ...testChildRoutes,
      ],
    },
    ...testTopRoutes,
    {
      path: '/tray',
      name: 'tray',
      component: () => import('@/views/tray/index.vue'),
      meta: {
        window: 'tray-panel',
      },
    },
    {
      path: '/notice',
      name: 'notice',
      component: () => import('@/views/notice/index.vue'),
      meta: {
        window: 'notice',
      },
    },
  ],
})

// ==================== 路由守卫 ====================

router.beforeEach((to, from, next) => {
  // 1. 设备类型校验
  deviceTypeGuard(to, from, next)
})

router.beforeEach((to, from, next) => {
  // 2. admin-only 页面权限校验
  adminOnlyGuard(to, from, next)
})

router.beforeEach((to, from, next) => {
  // 3. 键盘改键 / 行程可见性校验
  keyboardFeatureGuard(to, from, next)
})

// 按需监听权限变化：
// 只在受限页面创建 watch，进入普通页面时注销，跳转后自动注销
let adminOnlyStopWatch: WatchStopHandle | null = null
let keyboardFeatureStopWatch: WatchStopHandle | null = null

router.afterEach((to) => {
  // 搜索引擎只应收录公开首页；设备、登录、子窗口和测试路由均为工具页面。
  let robotsMeta = document.querySelector<HTMLMetaElement>('meta[name="robots"]')
  if (!robotsMeta) {
    robotsMeta = document.createElement('meta')
    robotsMeta.name = 'robots'
    document.head.appendChild(robotsMeta)
  }
  robotsMeta.content = to.meta?.indexable ? 'index, follow' : 'noindex, nofollow'

  // 先注销上个页面的监听
  adminOnlyStopWatch?.()
  adminOnlyStopWatch = null
  keyboardFeatureStopWatch?.()
  keyboardFeatureStopWatch = null

  // 当前页面有 adminOnly 标记 → 创建 watch 监听权限变化
  if (to.meta?.adminOnly) {
    const { canShowBaseScreenContent } = useAdminOnly()
    adminOnlyStopWatch = watch(canShowBaseScreenContent, (val) => {
      if (!val) {
        adminOnlyStopWatch?.() // 跳转前先注销自己
        adminOnlyStopWatch = null
        router.replace({ name: 'base-light' })
      }
    })
  }

  // 改键 / 行程：登录成网吧管理员后若仍停在这两页，立刻退回灯光
  if (to.name === 'keyboard-keys') {
    const { canShowKeyboardKeys } = useAdminOnly()
    keyboardFeatureStopWatch = watch(canShowKeyboardKeys, (val) => {
      if (!val) {
        keyboardFeatureStopWatch?.()
        keyboardFeatureStopWatch = null
        router.replace({ name: 'keyboard-light' })
      }
    })
  }
  if (to.name === 'keyboard-itinerary') {
    const { canShowKeyboardItinerary } = useAdminOnly()
    keyboardFeatureStopWatch = watch(canShowKeyboardItinerary, (val) => {
      if (!val) {
        keyboardFeatureStopWatch?.()
        keyboardFeatureStopWatch = null
        router.replace({ name: 'keyboard-light' })
      }
    })
  }
})

export default router
